import React, {useCallback, useState} from 'react';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Alert from '@material-ui/lab/Alert';
import {GoogleReCaptcha} from 'react-google-recaptcha-v3';
import DialogTitle from '@material-ui/core/DialogTitle';
import CircularProgress from "@material-ui/core/CircularProgress";
import {FormControlLabel, FormGroup, makeStyles, Switch} from "@material-ui/core";
import {StyledInput} from "../../Typeset";
import {Elements, StripeProvider} from "react-stripe-elements";
import TransferCheckoutForm from "./TransferCheckoutForm";
import {api} from "../../../api";
import TransferProcess from "../../TransferProcess";

const useStyles = makeStyles({
    root: {
        backgroundColor: 'rgb(84, 86, 95)',
        color: 'white'
    },
    dialogRoot: {
        '& .MuiDialog-paper': {
            margin: 16,
        }
    },
    buttonRow: {
        backgroundColor: 'rgb(84, 86, 95)',
        color: 'white',
        padding: 24,
    },
    switchLabel: {
        marginLeft: 0,
        '& .MuiTypography-body1': {
            fontSize: 14,
        }
    },
    textWhite: {
        color: 'white',
        fontSize: 14,
        lineHeight: 1.5,
        fontWeight: 400,
    },
    buttonStyleFilled: {
        background: '#F62459',
        color: '#FFF',
        padding: '8px 20px',
        '&:hover': {
            background: '#C93756'
        },
    },
});

export default function SellTicketPopup({
    isOpen,
    handleClose,
    handleSubmit,
    currency,
    actualPrice,
    actualBookingFee,
    firstName,
    lastName,
    ticketTier,
    eventTitle,
    hash,
    ticket_transfer_price,
    show_in_marketplace,
    max_transfer_price,
    isTransferFeePaid,
    handleSubmitUpdate,
}) {
    const classes = useStyles();
    const [stripeOptions, setStripeOptions] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showInMarketplace, setShowInMarketplace] = useState(isTransferFeePaid ? show_in_marketplace : true);
    const [ticketTransferPrice, setTicketTransferPrice] = useState(ticket_transfer_price || max_transfer_price || actualPrice);
    const [captchaToken, setCaptchaToken] = React.useState('');

    const isTransferLessThanErr = (isNaN(ticketTransferPrice) || ticketTransferPrice < (actualPrice / 2));
    const isTransferGreaterThanErr = ticketTransferPrice > max_transfer_price;

    const prepareStripe = async () => {
        setIsLoading(true);
        try {
            const res = await api.post(
                'triniq_ticket/v1/prepare_stripe_transfer_ticket/',
                {
                    hash,
                }
            );
            setStripeOptions(res.data);
        } catch (e) {
            alert("Payment failed. Please contact us at support@triniq.com");
        }
    }

    const updatePrice = useCallback(async () => {
        try {
            const res = await api.post(
                'triniq_ticket/v1/set_ticket_transfer_price/',
                {
                    hash,
                    ticket_transfer_price: ticketTransferPrice,
                    show_in_marketplace: showInMarketplace,
                    captcha: captchaToken,
                }
            );
            handleSubmitUpdate(res.data);
            alert("Ticket options updated successfully.");
        } catch (e) {
            if (e.response.data && e.response.data.error) {
                alert(e.response.data.error);
            } else {
                alert("There was an error updating the options. Please contact us at support@triniq.com");
            }
        }
    }, [ticketTransferPrice, showInMarketplace, captchaToken]);

    const StepTwo = ({stripe_pk, stripe_pi_cs, price_str, ticket_description}) => (
        <div style={{marginTop: 12}}>
            <StripeProvider apiKey={stripe_pk}>
                <Elements>
                    <TransferCheckoutForm
                        onCancel={() => {
                            setStripeOptions(null);
                            setIsLoading(false);
                        }}
                        onSuccess={(payload) => handleSubmit({...payload, ticketTransferPrice, showInMarketplace})}
                        clientIntentClientSecret={stripe_pi_cs}
                        {...{firstName, lastName, price_str, ticket_description}}
                    />
                </Elements>
            </StripeProvider>
        </div>
    )

    return (
        <Dialog open={isOpen} onClose={handleClose} className={classes.dialogRoot} aria-labelledby="form-dialog-title">
            <DialogTitle className={classes.root} id="form-dialog-title">TRANSFER TICKET</DialogTitle>
            <DialogContent className={classes.root} style={{padding: 0}}>
                <div className={classes.textWhite} style={{minWidth: '280px',}}>
                    <div style={{backgroundColor: '#4a5557', padding: '12px 24px'}}>
                        <div style={{fontSize: '0.8rem', marginBottom: 6}}>Adjust sale price:</div>
                        <div style={{position: 'relative',}}>
                        <span style={{
                            pointerEvents: 'none',
                            position: 'absolute',
                            top: '21px',
                            lineHeight: 0,
                            left: '9px'
                        }}>{currency.slice(-1)}</span>
                            <StyledInput
                                style={{width: '100px'}}
                                type="number"
                                value={ticketTransferPrice}
                                placeholder={firstName}
                                onChange={(e) => setTicketTransferPrice(e.target.value)}
                            />
                            {isTransferLessThanErr &&
                                <Alert severity="error" style={{marginBottom: 10}}>Sale price must be greater
                                    than {currency.slice(-1)}{actualPrice / 2}</Alert>
                            }
                            {isTransferGreaterThanErr &&
                                <Alert severity="error" style={{marginBottom: 10}}>
                                    Sale price must be less
                                    than {currency.slice(-1)}{max_transfer_price}
                                </Alert>
                            }
                            <span style={{
                                pointerEvents: 'none',
                                position: 'absolute',
                                top: '21px',
                                lineHeight: 0,
                                left: '110px'
                            }}>
                                {'+'} {currency.slice(-1)}{actualBookingFee} booking fee
                            </span>
                        </div>
                        <FormControlLabel
                            className={classes.switchLabel}
                            control={<Switch
                                size="small"
                                checked={showInMarketplace}
                                onChange={() => setShowInMarketplace(!showInMarketplace)}
                            />}
                            label="Visible on public marketplace"
                        />
                        <br/>
                        <br/>
                        Ticket: <b>{eventTitle}</b> - <b>{ticketTier}</b><br/>
                        Name on Ticket: <b>{firstName} {lastName}</b> <br/>
                        Tickets will be listed
                        for: <b>{currency.slice(-1)}{parseFloat(ticketTransferPrice) + parseFloat(actualBookingFee)}</b>
                        <br/>
                    </div>
                    <div style={{padding: '0px 24px 12px 24px'}}>
                        {stripeOptions === null ?
                            <TransferProcess
                                currency={currency}
                                ticketTransferPrice={ticketTransferPrice}
                                actualPrice={actualPrice}/> :
                            StepTwo(stripeOptions)
                        }
                    </div>
                </div>
                <GoogleReCaptcha onVerify={setCaptchaToken} refreshReCaptcha={false}/>
            </DialogContent>
            {stripeOptions === null && <DialogActions className={classes.buttonRow}>
                <Button className={classes.textWhite} onClick={handleClose} color="primary">
                    Cancel
                </Button>
                {isTransferFeePaid ?
                    (
                        <Button
                            onClick={() => updatePrice()}
                            color="primary"
                            className={classes.buttonStyleFilled}
                            disabled={isLoading || isTransferLessThanErr || isTransferGreaterThanErr}
                            style={{width: '120px'}}
                        >
                            {isLoading ? <CircularProgress style={{color: 'white'}} size={24}/> : 'UPDATE'}
                        </Button>
                    ) :
                    (
                        <Button
                            onClick={() => prepareStripe()}
                            color="primary"
                            className={classes.buttonStyleFilled}
                            disabled={isLoading || isTransferLessThanErr || isTransferGreaterThanErr}
                            style={{width: '120px'}}
                        >
                            {isLoading ? <CircularProgress style={{color: 'white'}} size={24}/> : 'PROCEED'}
                        </Button>
                    )
                }
            </DialogActions>}
        </Dialog>
    );
}
